import {
  Alert,
  Button,
  ButtonGroup,
  Chip,
  ClickAwayListener,
  Collapse,
  Grow,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { GET_GUIDE_MIGRATIONS, PUBLISH_GUIDE_MIGRATION, PUBLISH_GUIDE_MIGRATION_AS_PDF_ONLY } from './guideArchive.graphql';
import {
  AccessTime,
  ArrowDropDown,
  CheckCircle,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  Publish,
  Visibility,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatDate } from '../Formatters';
import { useConfig } from '../env';
import { useModal } from '../dialogs/useModal';
import { ConfirmDialog, ConfirmDialogProps } from '../dialogs/ConfirmDialog';
import { GuideGroup, Migration } from './types';
import { TableCellEllipsis } from '../components/TableComponents';
import { getShortName } from '../utils/string';

export const GuideRow: FC<{ group: GuideGroup; isOpen: boolean; onToggle: () => void }> = ({ group, isOpen, onToggle }) => {
  const config = useConfig();
  const { migrations, publishedCount, allPublished } = group;
  const [publishingId, setPublishingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [menuMigrationId, setMenuMigrationId] = React.useState<string | null>(null);
  const anchorRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [publishMigration] = useMutation(PUBLISH_GUIDE_MIGRATION, {
    refetchQueries: [GET_GUIDE_MIGRATIONS],
  });
  const [publishMigrationAsPdfOnly] = useMutation(PUBLISH_GUIDE_MIGRATION_AS_PDF_ONLY, {
    refetchQueries: [GET_GUIDE_MIGRATIONS],
  });
  const confirmModal = useModal<ConfirmDialogProps, boolean>({
    data: { title: '', message: '' },
  });

  const handlePublish = async (migration: Migration, pdfOnly: boolean) => {
    const isRepublish = !!migration?.publishedAt;
    const label = pdfOnly ? 'Publiser kun gammel PDF' : 'Publiser';
    const confirmed = await confirmModal.open({
      title: isRepublish ? `${label} på nytt` : label,
      message: isRepublish
        ? `Er du sikker på at du vil publisere v${migration?.version} av ${migration?.docName} på nytt${pdfOnly ? ' (kun PDF)' : ''}?`
        : `Er du sikker på at du vil publisere v${migration?.version} av ${migration?.docName}${pdfOnly ? ' (kun PDF)' : ''}?`,
      submitText: label,
    });
    if (!confirmed) return;

    const mutate = pdfOnly ? publishMigrationAsPdfOnly : publishMigration;
    setPublishingId(migration!.id);
    try {
      await mutate({ variables: { input: { id: migration!.id } } });
    } catch {
      setError(`Kunne ikke publisere v${migration?.version} av ${migration?.docName}`);
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <>
      {confirmModal.isOpen && <ConfirmDialog modal={confirmModal} />}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <TableRow
        hover
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          '& > *': { borderBottom: 'unset !important' },
          ...(allPublished && { bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }),
        }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small">
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" fontWeight="medium">
            {migrations[0]?.docName}
          </Typography>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="body2">{migrations[0]?.title}</Typography>
        </TableCell>
        <TableCellEllipsis title={group.ownerName ?? undefined}>
          {getShortName(group.ownerName)}
        </TableCellEllipsis>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 100 }}>
            {allPublished ? (
              <>
                <LinearProgress variant="determinate" value={100} color="success" sx={{ flex: 1, height: 8, borderRadius: 1 }} />
                <CheckCircle color="success" fontSize="small" />
              </>
            ) : (
              <>
                <LinearProgress
                  variant="determinate"
                  value={(publishedCount / migrations.length) * 100}
                  sx={{ flex: 1, height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 30 }}>
                  {publishedCount}/{migrations.length}
                </Typography>
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Table size="small" sx={{ my: 2 }}>
              <TableBody>
                {migrations.map((migration) => {
                  const isPublished = !!migration?.publishedAt;
                  const isPublishingThis = publishingId === migration?.id;
                  return (
                    <TableRow key={migration?.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          v{migration?.version}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isPublished ? (
                          <Chip
                            label={`${migration.publishedAsPdfOnly ? 'Publisert som PDF' : 'Publisert fra CMS'} ${formatDate(migration.publishedAt)}`}
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                          />
                        ) : (
                          <Chip label="Ikke publisert" size="small" variant="outlined" icon={<AccessTime />} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5}>
                          <Tooltip title="Forhåndsvisning">
                            <IconButton
                              size="small"
                              href={`${config?.portalUrl}/dokument/${migration?.docId}?mode=preview&version=${migration?.version}`}
                              target="_blank"
                              component="a">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Åpne i byggforsk.no">
                            <IconButton
                              size="small"
                              href={`${config?.portalUrl}/dokument/${migration?.docId}?version=${migration?.version}`}
                              target="_blank"
                              component="a">
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ width: '100%' }}>
                        <Stack direction="row" gap={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            disabled={isPublishingThis}
                            component={Link}
                            to={`/archive-guide-editor/${migration?.id}`}>
                            Rediger
                          </Button>
                          <ButtonGroup variant="contained" size="small" ref={(el) => (anchorRefs.current[migration!.id] = el)}>
                            <Button
                              startIcon={<Publish />}
                              disabled={isPublishingThis}
                              onClick={(e) => {
                                e.stopPropagation();
                                migration && handlePublish(migration, false);
                              }}>
                              {isPublishingThis ? 'Publiserer...' : 'Publiser'}
                            </Button>
                            <Button
                              disabled={isPublishingThis}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuMigrationId((prev) => (prev === migration!.id ? null : migration!.id));
                              }}>
                              <ArrowDropDown />
                            </Button>
                          </ButtonGroup>
                          <Popper
                            open={menuMigrationId === migration!.id}
                            anchorEl={anchorRefs.current[migration!.id]}
                            transition
                            disablePortal
                            sx={{ zIndex: 1 }}>
                            {({ TransitionProps, placement }) => (
                              <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                                <Paper>
                                  <ClickAwayListener onClickAway={() => setMenuMigrationId(null)}>
                                    <MenuList autoFocusItem>
                                      <MenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setMenuMigrationId(null);
                                          migration && handlePublish(migration, true);
                                        }}>
                                        Publiser kun gammel PDF
                                      </MenuItem>
                                    </MenuList>
                                  </ClickAwayListener>
                                </Paper>
                              </Grow>
                            )}
                          </Popper>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
